import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GenericService } from '../../generic.service';
import { FormsModule } from '@angular/forms';
import { LandingService } from '../../landing-page/service/landing.service';
import { TroubleShootEultService } from '../service/trouble-shoot-eult.service';
import {
  MachineIssueListItem,
  TroubleShootingImage,
} from '../interface/troubleshoot-eult.interface';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../shared/service/language.service';
import { HeaderComponent } from '../../shared/header/header.component';
import { defaultLanguage } from '../../shared/constants/language.constants';
import { CrowdinService } from '../../shared/service/crowdin.service';

@Component({
    selector: 'app-steps-eult',
    imports: [CommonModule, RouterModule, FormsModule, HeaderComponent],
    templateUrl: './steps-eult.component.html',
    styleUrls: ['./steps-eult.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class StepsEultComponent {
  stepId: string | null = '';
  step: any;
  stepsList: any;
  currentStepIndex: number = 0;
  issueName: string | undefined;
  deviceData: any = {};
  deviceData$: any = null;
  popupDetails: {
    showPopup: boolean;
    isImage: boolean;
    url?: string;
    images: TroubleShootingImage[];
    show: boolean;
    isDo: boolean;
    message: string;
  } = {
    showPopup: false,
    isImage: true,
    url: '',
    images: [],
    show: false,
    isDo: false,
    message: '',
  };
  machineIssueList: MachineIssueListItem[] = [];
  accountNumber: string = '';
  languageDetails!: (item: any) => any;

  currentQuestion: MachineIssueListItem | null = null;
  popupImageIndex: number = 0;
  currentImages: TroubleShootingImage[] = [];
  languageIndex: number = 0;
  translations: { [key: string]: string } = {};
  currentLanguage: string = defaultLanguage;
  languageOptions: Array<{ value: string }> = [];
  isLoadingTranslations = true;
  subs = new Subscription();

  constructor(
    private troubleShootService: TroubleShootEultService,
    private route: ActivatedRoute,
    private router: Router,
    private genericService: GenericService,
    private landingService: LandingService,
    private crowdinService: CrowdinService,
  ) {
    this.deviceData$ = this.genericService.deviceData.subscribe((data) => {
      if (data) {
        this.deviceData = data;
      }
    });
  }

  ngOnInit() {
    this.languageOptions = this.landingService.getLanguageOptions();

    const stored = localStorage.getItem('deviceData');
    const selectedName = stored
      ? JSON.parse(stored).languageSelected || defaultLanguage
      : defaultLanguage;
    this.currentLanguage = selectedName;

    this.subs.add(
      this.landingService.languageName$.subscribe((langName) => {
        this.currentLanguage = langName;
        this.languageIndex =
          this.crowdinService.getLanguageIndexByName(langName);
        this.translations = this.crowdinService.getAllTranslations();
      }),
    );

    this.landingService.loadTranslations(this.currentLanguage);
    this.languageDetails = (item: any) => this.getTranslatedDetails(item);
    this.stepId = this.route.snapshot.paramMap.get('stepId');

    if (this.stepId) {
      this.step = this.troubleShootService.getTroubleShootStep(this.stepId);

      if (this.step) {
        this.issueName = this.troubleShootService.getIssuename();
      } else {
        console.error(`No step found for ID: ${this.stepId}`);
      }

      this.stepsList = this.troubleShootService.getStepState();

      if (this.stepsList && this.stepsList.length) {
        this.currentStepIndex = this.troubleShootService.getStepIndex(
          this.stepId,
        );
      }

      this.loadMachineIssuelist();
    }
  }

  async languageChange(event: any) {
    const selectedLang = event.target.value;
    await this.landingService.loadTranslations(selectedLang);
  }

  resetIssueList() {
    this.machineIssueList.forEach((question) => {
      question.status = undefined;
      question.answered = false;
      question.expanded = false;
    });
  }

  loadMachineIssuelist() {
    if (
      !this.step ||
      !this.step.machineIssueListItems ||
      this.step.machineIssueListItems.length === 0
    ) {
      return;
    }

    this.machineIssueList = [...this.step.machineIssueListItems];
    this.resetIssueList();
  }

  selection(option: any) {
    this.resetIssueList();
    this.machineIssueList = [];
    if (option) {
      if (option.key === 'elt-leaking') {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        const originalUrl: string = this.router.url;
        const parts: string[] = originalUrl.split('/');
        const lastPart: string | undefined = parts.pop();
        if (lastPart) {
          const replacedUrl: string = originalUrl.replace(
            lastPart,
            option.steps[0].key,
          );
          this.router.navigateByUrl(replacedUrl).then(() => {
            this.step = this.troubleShootService.getTroubleShootStep(
              option.steps[0].key,
            );
            const issueListStep = this.step?.steps?.find(
              (s: { key: string }) => s.key === 'elt-issueList-items',
            );

            if (issueListStep && issueListStep.machineIssueListItems?.length) {
              this.machineIssueList = issueListStep.machineIssueListItems;
            } else {
              this.machineIssueList = [];
            }
          });
        }
        return;
      }

      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.onSameUrlNavigation = 'reload';
      const originalUrl: string = this.router.url;
      const parts: string[] = originalUrl.split('/');
      const lastPart: string | undefined = parts.pop();
      if (lastPart) {
        const replacedUrl: string = originalUrl.replace(
          lastPart,
          option.steps[0].key,
        );
        this.router.navigateByUrl(replacedUrl).then(() => {
          this.step = this.troubleShootService.getTroubleShootStep(
            option.steps[0].key,
          );
          const issueListStep = this.step.steps?.find(
            (s: { key: string }) => s.key === 'elt-issueList-items',
          );

          if (issueListStep && issueListStep.machineIssueListItems) {
            this.machineIssueList = issueListStep.machineIssueListItems;
          } else {
            this.machineIssueList = [];
          }
        });
      }
    }
  }

  next() {
    this.currentStepIndex += 1;

    if (this.currentStepIndex < this.stepsList.length) {
      const nextStep = this.stepsList[this.currentStepIndex];
      if (nextStep?.key) {
        this.resetIssueList();
        this.router.navigate([nextStep.key]);
        this.loadMachineIssueListForStep(nextStep);
      }
    }
  }

  loadMachineIssueListForStep(step: any) {
    const issueListStep = step.steps?.find(
      (s: { key: string }) => s.key === 'elt-issueList-items',
    );
    this.machineIssueList = issueListStep
      ? issueListStep.machineIssueListItems
      : [];
    this.resetIssueList();
  }

  answer(index: number, status: string) {
    this.machineIssueList[index].status = status;
    this.machineIssueList[index].answered = true;
    if (this.machineIssueList[index].details) {
      this.machineIssueList[index].expanded = true;
    }
  }

  toggleAccordion(index: number) {
    this.machineIssueList[index].expanded =
      !this.machineIssueList[index].expanded;
  }

  start(option: any) {
    option = this.stepsList[1];

    if (option?.key) {
      let key = option?.key;

      this.router.routeReuseStrategy.shouldReuseRoute = function () {
        return false;
      };
      this.router.onSameUrlNavigation = 'reload';

      const originalUrl: string = this.router.url;
      const parts: string[] = originalUrl.split('/');
      const lastPart: string | undefined = parts.pop();
      if (lastPart) {
        const replacedUrl: string = originalUrl.replace(lastPart, key);
        this.router.navigateByUrl(replacedUrl);
      }
    }
  }

  resolved() {
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
    this.router.navigateByUrl('thankYou');
  }

  needHelp() {
    this.landingService.needHelp();
  }

  openImagesPopup(question: MachineIssueListItem) {
    this.currentQuestion = question;
    if (question?.details?.images && question.details.images.length > 0) {
      this.currentImages = question.details.images.slice(0, 2).map((img) => ({
        url: img.url,
        alt: 'Image',
      }));
      this.popupImageIndex = 0;
      this.popupDetails = {
        showPopup: true,
        isImage: true,
        url: '',
        images:
          question.details.images?.map((img) => ({
            url: img.url,
            alt: 'Image',
          })) || [],
        show: true,
        isDo: false,
        message: '',
      };
    }
  }

  closePopup() {
    this.popupDetails.showPopup = false;
  }

  onNextImages() {
    const images = this.popupDetails.images || [];
    if (this.popupImageIndex + 2 < images.length) {
      this.popupImageIndex += 2;
      this.currentImages = images.slice(
        this.popupImageIndex,
        this.popupImageIndex + 2,
      );
    }
  }

  onPreviousImages() {
    if (this.popupImageIndex - 2 >= 0) {
      this.popupImageIndex -= 2;
      this.currentImages = this.popupDetails.images.slice(
        this.popupImageIndex,
        this.popupImageIndex + 2,
      );
    }
  }

  requestHelp(option: any) {
    const accountNumber = this.deviceData?.accountNumber || this.accountNumber;
    this.deviceData['issueName'] = this.issueName;
    this.deviceData['languageSelected'] = this.currentLanguage;
    this.genericService.setLocalStorage(this.deviceData);
    this.genericService.deviceData.next(this.deviceData);
    this.router.navigate([`/esrSupport/${accountNumber}`]);
  }

  getTranslatedTitle(item: any) {
    const key = this.currentLanguage.toLowerCase();
    return item.title[key];
  }

  getTranslatedDetails(item: any) {
    const key = this.currentLanguage.toLowerCase();
    return item.details[key];
  }

  get progress() {
    const answered = this.machineIssueList.filter(
      (item) => item.answered,
    ).length;
    return (answered / this.machineIssueList.length) * 100;
  }
}
